import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import DateFilter from "../components/DateFilter";
import {
  Phone, CheckCircle, AlertTriangle, Ban, Clock, Globe,
  XCircle, TrendingUp, ArrowRight, Bot, ShoppingCart, RefreshCw, Users,
  ShieldAlert,
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [stats, setStats] = useState({
    total_calls: 0, valid_calls: 0, fake_calls: 0, non_us_calls: 0,
    adult_calls: 0, ivr_calls: 0, buy_sell_calls: 0, pending_calls: 0,
  });
  const [chartData, setChartData] = useState({ daily_quality: [], publisher_risk: [] });
  const [behavioralSpamCount, setBehavioralSpamCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [chartsReady, setChartsReady] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const chartsReadyRef = useRef(false);

  const fetchData = useCallback(async (silent = false) => {
    if (!dateFilter) return;
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      params.set("start_date", dateFilter.startDate);
      params.set("end_date", dateFilter.endDate);

      // Fetch stats, chart data and the date-scoped behavioral-spam count
      // in parallel. The spam count follows the same global date filter as
      // Fake Calls / Adult Content — only callers who made attempts inside
      // this window are counted. The lifetime journey view remains untouched
      // and continues to fetch cross-time evidence from a separate endpoint.
      const [statsRes, chartRes, spamRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats?${params}`, { headers }),
        axios.get(`${API}/dashboard/chart-data?${params}`, { headers }),
        axios.get(`${API}/calls/behavioral-spam-count?${params}`, { headers }).catch(() => ({ data: { count: 0 } })),
      ]);

      setStats(statsRes.data);
      setChartData(chartRes.data);
      setBehavioralSpamCount(spamRes.data?.count || 0);
      setLastUpdated(new Date());
      if (!chartsReadyRef.current) {
        chartsReadyRef.current = true;
        requestAnimationFrame(() => setChartsReady(true));
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!dateFilter) return;
    intervalRef.current = setInterval(() => fetchData(true), 60000); // Refresh every 60s (was 30s)
    return () => clearInterval(intervalRef.current);
  }, [dateFilter, fetchData]);

  const problemCalls = stats.fake_calls + stats.non_us_calls + stats.adult_calls + (stats.ivr_calls || 0) + (stats.buy_sell_calls || 0);
  const validPercent = stats.total_calls > 0 ? Math.round((stats.valid_calls / stats.total_calls) * 100) : 0;
  const problemPercent = stats.total_calls > 0 ? Math.round((problemCalls / stats.total_calls) * 100) : 0;

  const pieData = useMemo(() => [
    { name: "Valid", value: stats.valid_calls, color: "#10b981" },
    { name: "Fake", value: stats.fake_calls, color: "#ef4444" },
    { name: "Non-US", value: stats.non_us_calls, color: "#f59e0b" },
    { name: "Adult", value: stats.adult_calls, color: "#8b5cf6" },
    { name: "IVR/Robot", value: stats.ivr_calls || 0, color: "#3b82f6" },
    { name: "Buy/Sell", value: stats.buy_sell_calls || 0, color: "#06b6d4" },
    { name: "Pending", value: stats.pending_calls, color: "#eab308" },
  ].filter(d => d.value > 0), [stats]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#11141D] border border-white/5 rounded-lg px-3 py-2 shadow-xl">
          <p className="text-xs text-slate-400">{payload[0].name}</p>
          <p className="text-sm font-semibold text-white">{payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  const breakdownItems = [
    { label: "Fake Calls", desc: "Spam, scam, irrelevant", value: stats.fake_calls, color: "red", icon: XCircle, route: "/call-analysis?category=fake" },
    { label: "Non-US Accent", desc: "Foreign callers", value: stats.non_us_calls, color: "orange", icon: Globe, route: "/call-analysis?category=non_us_accent" },
    { label: "Adult Calls", desc: "Inappropriate content", value: stats.adult_calls, color: "purple", icon: Ban, route: "/call-analysis?category=adult_call" },
    { label: "IVR/Robot", desc: "Automated callers", value: stats.ivr_calls || 0, color: "blue", icon: Bot, route: "/call-analysis?category=ivr_call" },
    { label: "Buy/Sell Intent", desc: "Trying to sell products", value: stats.buy_sell_calls || 0, color: "cyan", icon: ShoppingCart, route: "/call-analysis?category=buy_sell_intent" },
    { label: "Blacklisted Products", desc: "Restricted product mention", value: stats.blacklisted_calls || 0, color: "rose", icon: Ban, route: "/call-analysis?category=blacklisted_product" },
    { label: "Behavioral Spam", desc: "2+ offers OR 10+ calls in 5min — grouped by caller", value: behavioralSpamCount, color: "rose", icon: ShieldAlert, route: "/call-analysis?category=behavioral_spam" },
    { label: "Pending Analysis", desc: "Awaiting AI review", value: stats.pending_calls, color: "yellow", icon: Clock, route: "/call-analysis?category=pending" },
  ];

  const publisherRisk = chartData.publisher_risk || [];
  const fraudFeed = chartData.fraud_feed || [];

  // Category → visual config for fraud feed rows
  const feedCategoryConfig = {
    adult_call:      { label: "Adult Call",       dot: "#a855f7", chip: "bg-purple-500/10 text-purple-300 border-purple-500/20" },
    spam_call:       { label: "Spam Call",        dot: "#ef4444", chip: "bg-red-500/10 text-red-300 border-red-500/20" },
    dead_call:       { label: "Dead Call",        dot: "#64748b", chip: "bg-slate-500/10 text-slate-300 border-slate-500/20" },
    non_us_accent:   { label: "Non-US Accent",    dot: "#f59e0b", chip: "bg-orange-500/10 text-orange-300 border-orange-500/20" },
    ivr_call:        { label: "IVR/Robocaller",   dot: "#3b82f6", chip: "bg-blue-500/10 text-blue-300 border-blue-500/20" },
    buy_sell_intent: { label: "Buy/Sell",         dot: "#06b6d4", chip: "bg-cyan-500/10 text-cyan-300 border-cyan-500/20" },
  };

  const timeAgo = (iso) => {
    try {
      const then = new Date(iso.replace(" ", "T") + "Z");
      const seconds = Math.max(0, (Date.now() - then.getTime()) / 1000);
      if (seconds < 60) return "just now";
      if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
      return `${Math.floor(seconds / 86400)}d ago`;
    } catch {
      return "";
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Ringba Sync Error Warning */}
      {authUser?.ringba_sync_error && (
        <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3" data-testid="ringba-sync-error">
          <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-400">Ringba Sync Error</p>
            <p className="text-xs text-red-400/70 mt-0.5">{authUser.ringba_sync_error}</p>
            <p className="text-xs text-slate-500 mt-1">Check your Ringba API key in <button onClick={() => navigate("/profile")} className="underline text-blue-400 hover:text-blue-300">Profile Settings</button></p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Dashboard</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Call quality overview
            {lastUpdated && <span className="ml-1 sm:ml-2 text-slate-600">&middot; Updated {lastUpdated.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-[11px] font-semibold text-emerald-400 tracking-wide uppercase">Live Data</span>
          </div>
          <button onClick={() => fetchData()} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-2.5 py-1.5 transition-all" data-testid="refresh-dashboard">
            <RefreshCw className="h-3.5 w-3.5" /><span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      <DateFilter onFilterChange={setDateFilter} defaultPreset="today" />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Card className="bg-[#11141D] border-white/5 hover:border-blue-500/20 hover:-translate-y-0.5 transition-all duration-300">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Total Calls</p>
                    <p className="text-3xl font-bold text-white mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.total_calls}</p>
                    <p className="text-[11px] text-slate-500 mt-1.5">In selected period</p>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/15"><Phone className="h-6 w-6 text-blue-400" /></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#11141D] border-white/5 hover:border-emerald-500/20 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer" onClick={() => navigate('/call-analysis?category=valid_service_call')}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Valid Calls</p>
                    <p className="text-3xl font-bold text-white mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.valid_calls}</p>
                    <Badge className="mt-1.5 bg-emerald-500/10 text-emerald-400 border-0 text-[11px] px-2 py-0">{validPercent}% of total</Badge>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/15"><CheckCircle className="h-6 w-6 text-emerald-400" /></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#11141D] border-white/5 hover:border-red-500/20 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer" onClick={() => navigate('/call-analysis?category=fake')}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-red-400 uppercase tracking-wider">Problem Calls</p>
                    <p className="text-3xl font-bold text-white mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{problemCalls}</p>
                    <Badge className="mt-1.5 bg-red-500/10 text-red-400 border-0 text-[11px] px-2 py-0">{problemPercent}% of total</Badge>
                  </div>
                  <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/15"><AlertTriangle className="h-6 w-6 text-red-400" /></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown + Chart */}
          {chartsReady ? (
            <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="bg-[#11141D] border-white/5">
              <CardHeader className="pb-3 border-b border-white/5">
                <CardTitle className="text-sm font-semibold text-slate-200 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <AlertTriangle className="h-4 w-4 text-red-400" /> Problem Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-1.5">
                {breakdownItems.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between p-2.5 rounded-lg hover:bg-white/[0.03] cursor-pointer transition-all duration-200 group"
                    onClick={() => {
                      // Carry the current dashboard date filter through to
                      // the Analysis page so the count you clicked matches
                      // the list you land on. Only append if the row's route
                      // doesn't already contain a `start_date` param.
                      const params = new URLSearchParams();
                      if (dateFilter?.startDate) params.set("start_date", dateFilter.startDate);
                      if (dateFilter?.endDate) params.set("end_date", dateFilter.endDate);
                      const glue = item.route.includes("?") ? "&" : "?";
                      const target = params.toString()
                        ? `${item.route}${glue}${params.toString()}`
                        : item.route;
                      navigate(target);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className={`h-4 w-4 text-${item.color}-400`} />
                      <div>
                        <p className="text-sm font-medium text-slate-200">{item.label}</p>
                        <p className="text-[11px] text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className={`text-lg font-bold text-${item.color}-400`} style={{ fontFamily: 'Outfit, sans-serif' }}>{item.value}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-slate-600 group-hover:text-slate-400 transition-colors" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-[#11141D] border-white/5">
              <CardHeader className="pb-3 border-b border-white/5">
                <CardTitle className="text-sm font-semibold text-slate-200" style={{ fontFamily: 'Outfit, sans-serif' }}>Call Distribution</CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <div className="h-64 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} stroke="none">
                        {pieData.map((entry, i) => <Cell key={`cell-${i}`} fill={entry.color} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', color: '#94a3b8' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Live Fraud Alerts */}
          <Card className="bg-[#11141D] border-white/5" data-testid="live-fraud-feed">
            <CardHeader className="pb-3 border-b border-white/5">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-200 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  Live Fraud Alerts
                  <span className="text-[10px] font-normal text-slate-500 ml-1">last 24h</span>
                </CardTitle>
                <Badge
                  variant="outline"
                  className="cursor-pointer hover:bg-white/5 text-[11px] border-white/10 text-slate-400"
                  onClick={() => navigate('/call-analysis')}
                  data-testid="fraud-feed-view-all"
                >
                  View All
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-3 pb-2">
              <div className="max-h-[450px] overflow-y-auto pr-1 -mr-1 space-y-1.5" data-testid="fraud-feed-list">
                {fraudFeed.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <CheckCircle className="h-8 w-8 text-emerald-500/60 mb-2" />
                    <p className="text-sm text-slate-400">No fraud events in the last 24 hours</p>
                    <p className="text-[11px] text-slate-600 mt-1">You&apos;re all clear.</p>
                  </div>
                ) : (
                  fraudFeed.map((event, idx) => {
                    const cfg = feedCategoryConfig[event.category] || { label: event.category, dot: '#64748b', chip: 'bg-slate-500/10 text-slate-300 border-slate-500/20' };
                    return (
                      <button
                        key={`${event.sample_call_id || idx}`}
                        onClick={() => event.sample_call_id && navigate(`/call-analysis?call_id=${event.sample_call_id}`)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10 transition-all text-left group"
                        data-testid={`fraud-feed-row-${idx}`}
                      >
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cfg.dot, boxShadow: `0 0 8px ${cfg.dot}80` }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] text-slate-500 shrink-0 tabular-nums">
                              {timeAgo(event.latest_timestamp)}
                            </span>
                            <span className="text-sm font-medium text-slate-200 truncate">
                              {event.publisher_name || 'Unknown'}
                            </span>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${cfg.chip}`}>
                              {cfg.label}
                            </span>
                            {event.count > 1 && (
                              <span className="text-[10px] font-semibold text-red-400 bg-red-500/10 border border-red-500/20 px-1.5 py-0.5 rounded">
                                ×{event.count}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {event.latest_caller_number && (
                              <span className="text-[11px] text-slate-500 tabular-nums font-mono">
                                {event.latest_caller_number}
                              </span>
                            )}
                            {event.auto_blocked && (
                              <span className="text-[10px] text-emerald-500 flex items-center gap-1">
                                <Ban className="h-2.5 w-2.5" /> Auto-blocked
                              </span>
                            )}
                            {event.confidence > 0 && (
                              <span className="text-[10px] text-slate-600">
                                {event.confidence}% confidence
                              </span>
                            )}
                          </div>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 text-slate-600 group-hover:text-slate-400 shrink-0 transition-colors" />
                      </button>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
